import { useContext, useEffect } from "react";
import toast from "react-hot-toast";

import { LayoutContext } from "./context/LayoutContext";
import { useOnlineStatus } from "hooks/useOnlineStatus";
import {
  localGetAllServiceOrders,
  localSaveServiceOrder,
} from "libs/idb/service-orders-db";
import { API, graphqlOperation, Storage } from "aws-amplify";
import {
  executedServiceOrdersByServiceOrderID,
  getServiceOrder,
} from "graphql/queries";
import {
  createExecutedServiceOrder,
  createServiceOrderPhoto,
  updateExecutedServiceOrder,
  updateServiceOrder,
} from "graphql/mutations";
import LocalizedStrings from "react-localization";
import { serviceOrderStatus } from "config/constants";
import { dataURItoBlob } from "components/utils/functions";

const Content = ({ children }) => {
  const { layoutState, me } = useContext(LayoutContext);
  const online = useOnlineStatus();

  async function createPhoto(file) {
    const fileSrc = `photo_${file.id}.jpeg`;
    const blob = dataURItoBlob(file.photo);

    await Storage.put(`service-order-photos/${fileSrc}`, blob, {
      contentType: blob.type,
      level: "public",
    });

    await API.graphql({
      query: createServiceOrderPhoto.replaceAll("__typename", ""),
      variables: {
        input: {
          serviceOrderID: file.serviceOrderID,
          photo: fileSrc,
          tag: file.tag,
        },
      },
    });
  }

  const saveExecutions = async (list) => {
    await Promise.all(
      list.map(async (data) => {
        const serviceOrderData = await API.graphql(
          graphqlOperation(getServiceOrder, { id: data.serviceOrder.id })
        );
        const serviceOrderItem = serviceOrderData.data.getServiceOrder;

        if (
          !serviceOrderItem ||
          serviceOrderItem.status === serviceOrderStatus.canceled
        ) {
          await localSaveServiceOrder(data.serviceOrder.id, {
            ...data,
            sync: true,
          });
          throw new Error(strings.orderCanceled);
        }

        const executionData = await API.graphql(
          graphqlOperation(executedServiceOrdersByServiceOrderID, {
            serviceOrderID: data.serviceOrder.id,
          })
        );
        const executionItem =
          executionData.data.executedServiceOrdersByServiceOrderID.items[0];

        const objItems = {
          ...data.execution,
          id: undefined,
          createdAt: undefined,
          updatedAt: undefined,
          userExecutor: me.id,
          deletedAt: undefined,
          executedRoutines: [
            ...(data.execution.executedRoutines?.map((ex) => ({
              ...ex,
              activities: [
                ...(ex.activities?.map((act) => ({
                  ...act,
                  __typename: undefined,
                })) ?? []),
              ],
              __typename: undefined,
            })) ?? []),
          ],
          __typename: undefined,
        };

        await API.graphql({
          query: updateServiceOrder.replaceAll("__typename", ""),
          variables: {
            input: {
              id: data.serviceOrder.id,
              observations: data.serviceOrder.observations,
            },
          },
        });

        if (!executionItem) {
          await API.graphql({
            query: createExecutedServiceOrder.replaceAll("__typename", ""),
            variables: {
              input: {
                ...objItems,
              },
            },
          });
        } else {
          await API.graphql({
            query: updateExecutedServiceOrder.replaceAll("__typename", ""),
            variables: {
              input: {
                ...objItems,
                id: executionItem.id,
              },
            },
          });
        }
        if (data.photos?.length > 0) {
          await Promise.all(
            data.photos.map(async (photo) => await createPhoto(photo))
          );
        }
        await localSaveServiceOrder(data.serviceOrder.id, {
          ...data,
          photos: [],
          sync: true,
        });
      })
    );
  };

  useEffect(() => {
    let interval;
    if (!online)
      toast("🔴 Offline", {
        duration: Infinity,
        position: "bottom-right",
      });
    else {
      toast.dismiss();
      interval = setInterval(async () => {
        const localData = await localGetAllServiceOrders();
        if (
          !localData ||
          localData?.length <= 0 ||
          localData?.every((l) => l.sync)
        )
          return;
        toast.promise(saveExecutions(localData.filter((l) => !l.sync) ?? []), {
          loading: strings.sync,
          success: <b>{strings.syncSucceed}</b>,
          error: (err) => <b>{err?.message || strings.syncFailed}</b>,
        });
      }, 15000);
    }

    return () => {
      toast.dismiss();
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, me]);

  return (
    <div
      style={{
        ...styles.content,
        marginLeft: layoutState.sidebarVisible ? "22rem" : 0,
        paddingBottom: "4rem",
      }}
      className="wrapper d-flex flex-column"
    >
      <div className="body flex-grow-1 px-3">{children}</div>
    </div>
  );
};

const styles = {
  content: {
    marginBottom: "4rem",
    transition:
      "margin-left .15s,margin-right .15s,box-shadow 75ms,transform .15s,width .15s,z-index 0s ease .15s",
  },
};

const strings = new LocalizedStrings({
  es: {
    sync: "Sincronizando...",
    syncSucceed: "¡Sincronización completa!",
    syncFailed: "Sincronización fallida",
    orderCanceled: "No se pudo sincronizar la orden porque ya fue anulada",
  },
});

export default Content;
